import type { PostgrestError, RealtimeChannel, SupabaseClient } from '@supabase/supabase-js';
import { apiFetch } from '../api/http';
import { toAppError } from '../utils/appErrors';
import { getSupabaseClient } from './supabase';

/* ---------- Proposed patch types ---------- */

export type ProposedRoom = {
  name: string;
  type?: string | null;
  capacity?: number | null;
  layout?: string | null;
  layouts?: Array<{ layout: string; capacity: number }>;
  dimensions?: string | null;
  ceiling_height?: string | null;
  electrical?: string | null;
  rigging?: string | null;
  sound_patch?: string | null;
  door_size?: string | null;
  wifi_available?: boolean | null;
  wifi_notes?: string | null;
  hardline_available?: boolean | null;
  hardline_notes?: string | null;
  misc_notes?: string | null;
  indoor_outdoor?: string | null;
};

export type ProposedContact = {
  first_name?: string | null;
  last_name?: string | null;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  phone_type?: string | null;
  note?: string | null;
};

/* ---------- Research run type ---------- */

export type VenueResearchRun = {
  job_id: string;
  venue_query: string | null;
  job_kind?: string | null;
  status: string;
  progress: string | null;
  task: string | null;
  model: string | null;
  updated_at: string;
  last_heartbeat?: string | null;
  requested_by?: string | null;
  error?: string | null;
  job_log?: string | null;
  venue_id?: string | null;
  proposed_venue_patch?: Record<string, unknown> | null;
  proposed_rooms_patch?: ProposedRoom[] | null;
  proposed_contacts_patch?: ProposedContact[] | null;
  overwrite_existing?: boolean | null;
  matched_venue_id?: string | null;
};

const isVenueResearchRun = (row: any): row is VenueResearchRun =>
  !!row && typeof row === 'object' && 'job_id' in row;

const VENUE_RESEARCH_SELECT =
  'job_id,venue_query,status,progress,task,model,updated_at,last_heartbeat,requested_by,error,venue_id,overwrite_existing' as const;
const VENUE_RESEARCH_SELECT_WITH_PATCH = `${VENUE_RESEARCH_SELECT},proposed_venue_patch,proposed_rooms_patch,proposed_contacts_patch,matched_venue_id` as const;
const VENUE_RESEARCH_SELECT_WITH_METADATA = `${VENUE_RESEARCH_SELECT},created_at,overwrite_existing` as const;

/* ---------- Queries ---------- */

export async function fetchVenueResearchRuns(params: {
  requestedBy: string;
  limit?: number;
  supabase?: SupabaseClient;
}): Promise<{ data: VenueResearchRun[]; error: PostgrestError | null }> {
  const { requestedBy, limit = 20, supabase } = params;
  const client = supabase ?? getSupabaseClient();
  const { data, error } = await client
    .from('venue_research_runs')
    .select(VENUE_RESEARCH_SELECT)
    .eq('requested_by', requestedBy)
    .order('updated_at', { ascending: false })
    .limit(limit);
  const rows = Array.isArray(data) ? (data as any[]).filter(isVenueResearchRun) : [];
  return { data: rows, error: error ?? null };
}

type RealtimePayload = Parameters<RealtimeChannel['on']>[2];
type SubscribeCallback = Parameters<RealtimeChannel['subscribe']>[0];

export async function fetchVenueResearchRunsByVenue(params: {
  venueId: string;
  limit?: number;
  supabase?: SupabaseClient;
}): Promise<{ data: VenueResearchRun[]; error: PostgrestError | null }> {
  const { venueId, limit = 20, supabase } = params;
  const client = supabase ?? getSupabaseClient();
  if (!venueId) return { data: [], error: null };
  const { data, error } = await client
    .from('venue_research_runs')
    .select(VENUE_RESEARCH_SELECT_WITH_METADATA)
    .eq('venue_id', venueId)
    .order('created_at', { ascending: true })
    .limit(limit);
  const rows = Array.isArray(data) ? (data as any[]).filter(isVenueResearchRun) : [];
  return { data: rows, error: error ?? null };
}

export async function fetchVenueResearchRun(params: {
  jobId: string;
  supabase?: SupabaseClient;
}): Promise<{ data: VenueResearchRun | null; error: PostgrestError | null }> {
  const { jobId, supabase } = params;
  const client = supabase ?? getSupabaseClient();
  if (!jobId) return { data: null, error: null };
  const { data, error } = await client
    .from('venue_research_runs')
    .select(VENUE_RESEARCH_SELECT_WITH_PATCH)
    .eq('job_id', jobId)
    .maybeSingle();
  if (error) return { data: null, error };
  return { data: (data as VenueResearchRun) ?? null, error: null };
}

/* ---------- Realtime subscriptions ---------- */

export function subscribeToVenueResearchRuns(params: {
  requestedBy: string;
  handler: RealtimePayload;
  onStatusChange?: SubscribeCallback;
  supabase?: SupabaseClient;
  channelName?: string;
}): RealtimeChannel {
  const { requestedBy, handler, onStatusChange, supabase, channelName } = params;
  const client = supabase ?? getSupabaseClient();
  const channel = client.channel(channelName ?? `vrr:user:${requestedBy}`);
  channel
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'venue_research_runs',
        filter: `requested_by=eq.${requestedBy}`,
      },
      handler,
    )
    .subscribe(onStatusChange);
  return channel;
}

/* ---------- Apply overwrite ---------- */

export type RoomAction = {
  index: number;
  action: 'insert' | 'update';
  existingRoomId?: string;
  selectedFields?: string[];
  overrideValues?: Record<string, unknown>;
};

export type ContactAction = {
  index: number;
  action: 'insert' | 'update';
  existingContactId?: string;
  selectedFields?: string[];
  overrideValues?: Record<string, unknown>;
};

export async function applyVenueResearchOverwrite(params: {
  jobId: string;
  /** The ticked venue fields (and `logo`). Only these change; an empty list changes none. */
  selectedFields: string[];
  overrideValues?: Record<string, unknown>;
  rooms?: RoomAction[];
  contacts?: ContactAction[];
  onAuthRetry?: () => void;
  supabase?: SupabaseClient;
}): Promise<{ ok: boolean; error?: string; code?: string; duplicateVenueId?: string | null; duplicateVenueName?: string | null }> {
  const body: Record<string, unknown> = { selectedFields: params.selectedFields };
  if (params.overrideValues && Object.keys(params.overrideValues).length > 0) {
    body.overrideValues = params.overrideValues;
  }
  if (params.rooms && params.rooms.length > 0) {
    body.rooms = params.rooms;
  }
  if (params.contacts && params.contacts.length > 0) {
    body.contacts = params.contacts;
  }

  async function parseError(response: Response): Promise<{
    code?: string;
    duplicateVenueId?: string | null;
    duplicateVenueName?: string | null;
  }> {
    let code: string | undefined;
    let duplicateVenueId: string | null | undefined;
    let duplicateVenueName: string | null | undefined;
    try {
      const raw = await response.text();
      if (raw) {
        try {
          const parsed = JSON.parse(raw) as {
            error?: unknown;
            message?: unknown;
            code?: unknown;
            duplicate_venue_id?: unknown;
            duplicate_venue_name?: unknown;
          };
          code = parsed?.code == null ? undefined : String(parsed.code);
          duplicateVenueId = parsed?.duplicate_venue_id == null ? null : String(parsed.duplicate_venue_id);
          duplicateVenueName = parsed?.duplicate_venue_name == null ? null : String(parsed.duplicate_venue_name);
        } catch { /* response was not JSON */ }
      }
    } catch { /* response body was unavailable */ }
    return { code, duplicateVenueId, duplicateVenueName };
  }

  let response: Response;
  try {
    response = await apiFetch(`/admin/venue-runs/${params.jobId}/apply-overwrite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (error) {
    return {
      ok: false,
      error: toAppError(
        error,
        'Venue research changes could not be applied.',
        'venue_research.apply_overwrite',
        'mutation',
      ).message,
    };
  }

  if (!response.ok) {
    const parsed = await parseError(response);
    if (parsed.code === 'DUPLICATE_VENUE_NAME') {
      return {
        ok: false,
        error: 'A venue with this name already exists.',
        code: parsed.code,
        duplicateVenueId: parsed.duplicateVenueId ?? null,
        duplicateVenueName: parsed.duplicateVenueName ?? null,
      };
    }
    if (parsed.code === 'LOGO_SUPERSEDED') {
      return {
        ok: false,
        error: 'A newer lookup replaced this logo. Open that lookup’s results to use it.',
        code: parsed.code,
      };
    }
    return {
      ok: false,
      error: toAppError(
        { status: response.status, code: parsed.code },
        'Venue research changes could not be applied.',
        'venue_research.apply_overwrite',
        'mutation',
      ).message,
    };
  }
  return { ok: true };
}

/* ---------- Review data fetchers ---------- */

/** Fetch current venue fields for side-by-side review against proposed_venue_patch. */
const VENUE_REVIEW_FIELDS =
  'venue_name,website,address_line1,address_line2,city,state,postal_code,country,notes,inhouse_av_company,inhouse_av_email,inhouse_av_phone,total_meeting_space_sqft,meeting_rooms_count,is_outdoor,logo_public_url' as const;

export async function fetchVenueForReview(params: {
  venueId: string;
  supabase?: SupabaseClient;
}): Promise<{ data: Record<string, unknown> | null; error: PostgrestError | null }> {
  const client = params.supabase ?? getSupabaseClient();
  if (!params.venueId) return { data: null, error: null };
  const { data, error } = await client
    .from('venues')
    .select(VENUE_REVIEW_FIELDS)
    .eq('id', params.venueId)
    .maybeSingle();
  if (error) return { data: null, error };
  return { data: (data as Record<string, unknown>) ?? null, error: null };
}

/** Fetch current rooms for a venue for side-by-side review against proposed_rooms_patch. */
const ROOM_REVIEW_FIELDS =
  'id,name,type,capacity,layout,dimensions,ceiling_height,electrical,rigging,sound_patch,door_size,wifi_available,wifi_notes,hardline_available,hardline_notes,misc_notes,indoor_outdoor' as const;

export async function fetchVenueRoomsForReview(params: {
  venueId: string;
  supabase?: SupabaseClient;
}): Promise<{ data: Array<Record<string, unknown>>; error: PostgrestError | null }> {
  const client = params.supabase ?? getSupabaseClient();
  if (!params.venueId) return { data: [], error: null };
  const { data, error } = await client
    .from('venue_rooms')
    .select(ROOM_REVIEW_FIELDS)
    .eq('venue_id', params.venueId)
    .is('deleted_at', null)
    .order('position');
  if (error) return { data: [], error };
  return { data: (data as Array<Record<string, unknown>>) || [], error: null };
}

/** Fetch current contacts for a venue for side-by-side review against proposed_contacts_patch. */
const CONTACT_REVIEW_FIELDS =
  'id,first_name,last_name,title,email,phone,phone_type,note' as const;

export async function fetchVenueContactsForReview(params: {
  venueId: string;
  supabase?: SupabaseClient;
}): Promise<{ data: Array<Record<string, unknown>>; error: PostgrestError | null }> {
  const client = params.supabase ?? getSupabaseClient();
  if (!params.venueId) return { data: [], error: null };
  const { data, error } = await client
    .from('venue_contacts')
    .select(CONTACT_REVIEW_FIELDS)
    .eq('venue_id', params.venueId)
    .is('deleted_at', null)
    .order('position');
  if (error) return { data: [], error };
  return { data: (data as Array<Record<string, unknown>>) || [], error: null };
}

/** Find an active venue with the same name (case-insensitive), excluding the current venue. */
export async function findDuplicateVenueByName(params: {
  venueName: string;
  excludeVenueId: string;
  supabase?: SupabaseClient;
}): Promise<{ data: { id: string; venue_name: string | null } | null; error: PostgrestError | null }> {
  const client = params.supabase ?? getSupabaseClient();
  const venueName = (params.venueName || '').trim();
  if (!venueName || !params.excludeVenueId) return { data: null, error: null };
  const { data, error } = await client
    .from('venues')
    .select('id,venue_name')
    .is('deleted_at', null)
    .neq('id', params.excludeVenueId)
    .ilike('venue_name', venueName)
    .limit(10);
  if (error) return { data: null, error };
  const normalized = venueName.toLowerCase();
  const exact = ((data || []) as Array<{ id: string; venue_name: string | null }>).find(
    (row) => (row.venue_name || '').trim().toLowerCase() === normalized,
  );
  return { data: exact || null, error: null };
}
